/**
 * Statusi спровођења на обрасцу извештаја о спровођењу обавезе (активности + укупни статус).
 * Пет вредности; боје: зелена (2) / жута (1) / црвена (2).
 */

export const REPORT_IMPLEMENTATION_STATUS = {
  /** У потпуности — зелено */
  IN_FULL: 'IN_FULL',
  /** Значајан напредак — зелено */
  SIGNIFICANT_PROGRESS: 'SIGNIFICANT_PROGRESS',
  /** Ограничен напредак — жуто */
  LIMITED_PROGRESS: 'LIMITED_PROGRESS',
  /** Није започето — црвено */
  NOT_STARTED: 'NOT_STARTED',
  /** Са значајним кашњењем — црвено */
  MAJOR_DELAY: 'MAJOR_DELAY',
};

/** @type {Array<{ value: string, label: string }>} */
export const REPORT_IMPLEMENTATION_STATUS_SELECT_OPTIONS = [
  { value: '', label: 'Изаберите статус' },
  {
    value: REPORT_IMPLEMENTATION_STATUS.IN_FULL,
    label: 'У потпуности',
  },
  {
    value: REPORT_IMPLEMENTATION_STATUS.SIGNIFICANT_PROGRESS,
    label: 'Значајан напредак',
  },
  {
    value: REPORT_IMPLEMENTATION_STATUS.LIMITED_PROGRESS,
    label: 'Ограничен напредак',
  },
  {
    value: REPORT_IMPLEMENTATION_STATUS.NOT_STARTED,
    label: 'Није започето',
  },
  {
    value: REPORT_IMPLEMENTATION_STATUS.MAJOR_DELAY,
    label: 'Са значајним кашњењем',
  },
];

/** @type {Record<string, string>} */
export const REPORT_IMPLEMENTATION_STATUS_LABELS = {
  [REPORT_IMPLEMENTATION_STATUS.IN_FULL]: 'У потпуности',
  [REPORT_IMPLEMENTATION_STATUS.SIGNIFICANT_PROGRESS]: 'Значајан напредак',
  [REPORT_IMPLEMENTATION_STATUS.LIMITED_PROGRESS]: 'Ограничен напредак',
  [REPORT_IMPLEMENTATION_STATUS.NOT_STARTED]: 'Није започето',
  [REPORT_IMPLEMENTATION_STATUS.MAJOR_DELAY]: 'Са значајним кашњењем',
};

/** Вредности које смеју у select извештаја (без планирања: ACCEPTED, CREATED, …). */
export const REPORT_IMPLEMENTATION_STATUS_VALUE_SET = new Set(Object.values(REPORT_IMPLEMENTATION_STATUS));

/**
 * BE {@code ObligationActivity.activity_status}: нема UI вредности IN_FULL / MAJOR_DELAY.
 * Пре слањања на POST /obligation/activity/save-or-update преведи их на канонско enum.
 *
 * Усклађено са валидним enum-ом (BadRequest при IN_FULL).
 */
export function mapReportImplementationStatusToApiEnum(status) {
  const upper = String(status ?? '').trim().toUpperCase();
  switch (upper) {
    /** „У потпуности“ — нема посебног кода на BE → најближа позитивна завршена вредност. */
    case REPORT_IMPLEMENTATION_STATUS.IN_FULL:
      return 'COMPLETED';
    /** BE користи SIGNIFICANT_DELAY; UI „Са значајним кашњењем“ = MAJOR_DELAY. */
    case REPORT_IMPLEMENTATION_STATUS.MAJOR_DELAY:
      return 'SIGNIFICANT_DELAY';
    default:
      return upper;
  }
}

/**
 * Mapира унутрашњи activity_status на вредност за select извештаја; планирање → празно.
 * @param {string|null|undefined} stored
 * @returns {string}
 */
export function toReportImplementationSelectValue(stored) {
  const s = String(stored ?? '').trim().toUpperCase();
  if (!s) return '';
  if (REPORT_IMPLEMENTATION_STATUS_VALUE_SET.has(s)) return s;
  if (s === 'SIGNIFICANT_DELAY') return REPORT_IMPLEMENTATION_STATUS.MAJOR_DELAY;
  /** Кад смо IN_FULL сачували као COMPLETED — поново се приказује исти избор. */
  if (s === 'COMPLETED') return REPORT_IMPLEMENTATION_STATUS.IN_FULL;
  return '';
}

export function getReportImplementationStatusAgLabel(value) {
  const v = String(value ?? '').trim();
  if (!v) return 'Није унето';
  return REPORT_IMPLEMENTATION_STATUS_LABELS[v] || v;
}

/** Класа за chip/modal (исти префикс као у ObligationFormPage.css). */
export function getReportImplementationStatusAgChipClass(value) {
  const key = value && String(value).trim() !== '' ? String(value).trim() : 'empty';
  return `obligation-report-status-select obligation-report-status-select--${key}`;
}

/**
 * Статуси за које је обавезан опис досадашњих резултата (укупни статус).
 * „Није започето” — без обавезног описа; остали избори захтевају опис.
 * @param {string} status
 * @returns {boolean}
 */
export const reportOverallStatusRequiresProgressDescription = (status) =>
  Boolean(status) && status !== REPORT_IMPLEMENTATION_STATUS.NOT_STARTED;
